import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  InputAdornment,
  Alert,
  Switch,
  FormGroup,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  HelpOutline as HelpIcon,
  Euro as EuroIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { useEnumTranslation } from '../../utils/translateEnum';
import { useAuthProvider } from '../../auth/hooks/auth-provider';
import { zoullooColors } from '../../theme';
import { UserType } from '../../models/UserType';
import {
  Project,
  ProjectCreateRequest,
  ProjectDomain,
  MissionType,
  ProjectType,
  ProjectLocation,
  ProjectStatus,
  DurationType,
} from '../../models/Project';
import { Company, CompanyCreateDTO } from '../../models/Company';
import { useClient } from '../../hooks/useClient';
import CompanyAutocomplete from '../common/CompanyAutocomplete';
import CompanyForm from '../company/CompanyForm';
import { useCreateCompany } from '../../hooks/useCompany';
import { useCreateProject, usePatchProject, useUpdateProjectStatus } from '../../hooks/useProject';
import { createProjectPatchOperations } from '../../utils/projectPatchUtils';
import apiClient from '../../api/api-client';
import HtmlEditor from '../UI/HtmlEditor';
import ProjectDocuments from './ProjectDocuments';
import TextFieldWithCounter from '../UI/TextFieldWithCounter';

interface ProjectFormProps {
  initialData?: Partial<Project>;
  onSubmit: (data: ProjectCreateRequest, isDraft?: boolean) => void | Promise<void>;
  onSubmitProject?: (data: ProjectCreateRequest) => void | Promise<void>;
  isSubmitting: boolean;
  submitButtonText: string;
  isEdit?: boolean;
}


const ProjectForm: React.FC<ProjectFormProps> = ({
  initialData = {},
  onSubmit,
  onSubmitProject,
  isEdit = false,
}) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuthProvider();
  const { translateProjectDomain, translateMissionType } = useEnumTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Check if user is CSM or Admin
  const isInternalUser = user.type === UserType.Internal;
  
  // Check if user is a client
  const isClient = user.type === UserType.Client;
  
  // Get client data if user is a client
  const { data: clientData } = useClient();

  // Convert date string to YYYY-MM-DD format for input
  // Extract date part directly to avoid timezone conversion issues
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    // Just extract the date part (YYYY-MM-DD) without timezone conversion
    return dateString.split('T')[0];
  };

  // Convert YYYY-MM-DD format to ISO string
  const formatDateForSubmit = (dateString: string) => {
    if (!dateString) return undefined;
    return new Date(dateString).toISOString();
  };

  // Calculate date range constraints
  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 2);
  const maxDateString = maxDate.toISOString().split('T')[0];

  // Simple validation functions based on original form logic
  const validateDraft = (values: ProjectCreateRequest) => {
    const errors: Record<string, string> = {};
    
    if (!values.name?.trim()) {
      errors.name = t('validation.required');
    }
     if (!values.companyId) {
      errors.companyId = t('validation.required');
    }
    
    return errors;
  };

  const validateSubmission = (values: ProjectCreateRequest) => {
    const errors: Record<string, string> = {};
    
    // Required fields for all users
    if (!values.name?.trim()) {
      errors.name = t('validation.required');
    }
    if (!values.companyId) {
      errors.companyId = t('validation.required');
    }
    if (!values.type) {
      errors.type = t('validation.required');
    }
    if (!values.location) {
      errors.location = t('validation.required');
    }
    if (!values.objectives?.trim()) {
      errors.objectives = t('validation.required');
    }
    if (!values.presentationAndNeeds?.trim()) {
      errors.presentationAndNeeds = t('validation.required');
    }
    if (!values.talentHardSkills?.trim()) {
      errors.talentHardSkills = t('validation.required');
    }
    if (!values.talentSoftSkills?.trim()) {
      errors.talentSoftSkills = t('validation.required');
    }
    if (!values.desiredStartDate) {
      errors.desiredStartDate = t('validation.required');
    }
    
    // CSM/Admin specific required fields
    if (isInternalUser) {
      if (!values.domain) {
        errors.domain = t('validation.required');
      }
      if (values.feePercentage === undefined || values.feePercentage === null) {
        errors.feePercentage = t('validation.required');
      }
    }
    
    // Type-specific validations
    if (values.type === ProjectType.TimeAndMaterial) {
      if (!values.duration) {
        errors.duration = t('validation.required');
      }
      if (!values.durationType) {
        errors.durationType = t('validation.required');
      }
      if (values.fullTime === undefined || values.fullTime === null) {
        errors.fullTime = t('validation.required');
      }
      if (values.fullTime === false) {
        if (!values.daysPerPeriod || values.daysPerPeriod <= 0) {
          errors.daysPerPeriod = t('validation.required');
        } else {
          const maxDays = values.durationType === DurationType.Weeks ? 5 : 20;
          if (values.daysPerPeriod > maxDays) {
            errors.daysPerPeriod = values.durationType === DurationType.Weeks
              ? t('validation.maxDaysPerWeek')
              : t('validation.maxDaysPerMonth');
          }
        }
      }
    }

    // Budget is now optional for all project types (removed validation)

    // Location-specific validations
    if (values.location === ProjectLocation.Hybrid && !values.hybridDetails?.trim()) {
      errors.hybridDetails = t('validation.required');
    }
    
    return errors;
  };

  // Initial form values
  const getInitialValues = (): ProjectCreateRequest => ({
    name: initialData?.name || '',
    domain: initialData?.domain,
    missionType: initialData?.missionType,
    type: initialData?.type,
    location: initialData?.location,
    companyId: initialData?.companyId || (isClient && clientData?.companyId ? clientData.companyId : undefined),
    objectives: initialData?.objectives || '',
    presentationAndNeeds: initialData?.presentationAndNeeds || '',
    talentHardSkills: initialData?.talentHardSkills || '',
    talentSoftSkills: initialData?.talentSoftSkills || '',
    budget: initialData?.budget,
    dailyRate: initialData?.dailyRate,
    duration: initialData?.duration,
    durationType: initialData?.durationType,
    fullTime: initialData?.fullTime,
    daysPerPeriod: initialData?.daysPerPeriod,
    hybridDetails: initialData?.hybridDetails || '',
    teamIntegration: initialData?.teamIntegration ?? false,
    desiredStartDate: formatDateForInput(initialData?.desiredStartDate),
    endDate: formatDateForInput(initialData?.endDate),
    feePercentage: initialData?.feePercentage,
    externalCostsFeePercentage: initialData?.externalCostsFeePercentage,
    expensesFeePercentage: initialData?.expensesFeePercentage,
    processingFees: initialData?.processingFees,
  });

  // Company selection state
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(initialData?.company || (isClient && clientData?.company ? clientData.company : null));
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);

  // Draft project state (for document upload before submission)
  const [draftProjectId, setDraftProjectId] = useState<number | null>(initialData?.id || null);
  const isCreatingDraft = useRef(false);

  // Company creation hooks
  const createCompanyMutation = useCreateCompany();
  const createdCompanyIdRef = useRef<number | null>(null);

  // Project creation hook (for auto-draft creation)
  const createProjectMutation = useCreateProject();
  const patchProjectMutation = usePatchProject();
  const updateStatusMutation = useUpdateProjectStatus();

  // Formik setup with simple validation
  const formik = useFormik<ProjectCreateRequest>({
    initialValues: getInitialValues(),
    validate: validateSubmission, // Use simple validation for submit
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: false,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      await handleFormSubmit(values, false);
      setSubmitting(false);
    },
  });


  // Update selected company when client data or initial data changes
  useEffect(() => {
    if (isClient && clientData?.company && !selectedCompany) {
      setSelectedCompany(clientData.company);
      formik.setFieldValue('companyId', clientData.companyId);
    }
    
    if (initialData?.company && !selectedCompany) {
      setSelectedCompany(initialData.company);
    }
  }, [clientData?.company?.id, initialData?.company?.id, isClient]);

  // Handle company creation result
  useEffect(() => {
    if (createdCompanyIdRef.current && !companyDialogOpen) {
      formik.setFieldValue('companyId', createdCompanyIdRef.current);
      // Fetch the created company to set it as selected
      const fetchCreatedCompany = async () => {
        try {
          const response = await apiClient.get(`/companies/${createdCompanyIdRef.current}`);
          setSelectedCompany(response.data);
          createdCompanyIdRef.current = null;
        } catch (error) {
          console.error('Error fetching created company:', error);
        }
      };
      fetchCreatedCompany();
    }
  }, [companyDialogOpen]);

  // Handle form field changes with business logic
  const handleChange = (field: keyof ProjectCreateRequest, value: any) => {
    // Set the field value
    formik.setFieldValue(field, value);
    
    // Clear related fields when changing project type
    if (field === 'type') {
      if (value === ProjectType.FixedPrice) {
        // Clear Time & Material specific fields
        formik.setFieldValue('dailyRate', undefined);
        formik.setFieldValue('duration', undefined);
        formik.setFieldValue('durationType', undefined); // Don't pre-select
        formik.setFieldValue('fullTime', undefined); 
        formik.setFieldValue('daysPerPeriod', undefined);
      } else if (value === ProjectType.TimeAndMaterial) {
        // Clear Fixed Price specific fields
        formik.setFieldValue('budget', undefined);
        // Don't pre-select duration type or full/part time
        formik.setFieldValue('durationType', undefined);
        formik.setFieldValue('fullTime', undefined);
      }
    }
    
    // Clear daysPerPeriod when changing durationType or when setting fullTime to true
    if (field === 'durationType' && formik.values.fullTime === false) {
      formik.setFieldValue('daysPerPeriod', undefined);
    }
    if (field === 'fullTime' && value === true) {
      formik.setFieldValue('daysPerPeriod', undefined);
    }
    
    // Clear hybrid details when changing from hybrid to another location
    if (field === 'location' && value !== ProjectLocation.Hybrid) {
      formik.setFieldValue('hybridDetails', '');
    }
  };

  // Handle company selection
  const handleCompanyChange = (company: Company | null) => {
    setSelectedCompany(company);
    formik.setFieldValue('companyId', company?.id);
  };

  // Clean data based on business rules
  const cleanFormData = (values: ProjectCreateRequest): ProjectCreateRequest => {
    const cleanData: ProjectCreateRequest = {
      ...values,
      desiredStartDate: formatDateForSubmit(values.desiredStartDate || ''),
      endDate: formatDateForSubmit(values.endDate || ''),
    };

    // Remove fields based on project type
    if (cleanData.type === ProjectType.FixedPrice) {
      // Remove Time & Material specific fields
      delete cleanData.dailyRate;
      delete cleanData.duration;
      delete cleanData.durationType;
      delete cleanData.fullTime;
      delete cleanData.daysPerPeriod;
    } else if (cleanData.type === ProjectType.TimeAndMaterial) {
      // Remove Fixed Price specific fields
      delete cleanData.budget;
      
      // Remove daysPerPeriod if fullTime is true
      if (cleanData.fullTime === true) {
        delete cleanData.daysPerPeriod;
      }
    }

    // Remove hybrid details if location is not hybrid
    if (cleanData.location !== ProjectLocation.Hybrid) {
      delete cleanData.hybridDetails;
    }

    // Remove empty optional fields
    Object.keys(cleanData).forEach(key => {
      const value = cleanData[key as keyof ProjectCreateRequest];
      if (value === '' || value === null || value === undefined || 
          (Array.isArray(value) && value.length === 0)) {
        delete cleanData[key as keyof ProjectCreateRequest];
      }
    });

    return cleanData;
  };

  // Define field order for validation (matches form layout)
  const fieldOrder = [
    'name',
    'companyId',
    'domain',
    'missionType',
    'feePercentage',
    'externalCostsFeePercentage',
    'expensesFeePercentage',
    'processingFees',
    'objectives',
    'presentationAndNeeds',
    'talentHardSkills',
    'talentSoftSkills',
    'desiredStartDate',
    'endDate',
    'location',
    'hybridDetails',
    'type',
    'budget',
    'dailyRate',
    'duration',
    'durationType',
    'fullTime',
    'daysPerPeriod',
  ];

  // Function to scroll to first error field
  const scrollToFirstError = (errors: Record<string, string>) => {
    // Find the first field with error based on defined order
    const firstErrorField = fieldOrder.find(field => errors[field]);

    if (!firstErrorField) return;

    const element = document.querySelector(
      `[name="${firstErrorField}"], [data-field="${firstErrorField}"]`
    );

    if (element) {
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });

      // Focus after scroll animation completes
      setTimeout(() => {
        if (element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLSelectElement) {
          element.focus();
        } else {
          // For custom components (HtmlEditor, Autocomplete, etc.), find focusable element inside
          const focusable = element.querySelector<HTMLElement>(
            'input, textarea, select, [contenteditable="true"], .ProseMirror'
          );
          if (focusable) {
            focusable.focus();
          }
        }
      }, 500);
    }
  };

  // Create draft project for document upload (on-demand, not automatic)
  const handleCreateDraftForDocument = async () => {
    const name = formik.values.name?.trim();
    const companyId = formik.values.companyId;

    // Button should already be disabled if these are empty, but double-check
    if (!name || !companyId || isCreatingDraft.current) return;

    isCreatingDraft.current = true;

    try {
      const draftData: ProjectCreateRequest = {
        name,
        companyId,
      };

      const result = await createProjectMutation.mutateAsync(draftData);
      setDraftProjectId(result.id);
    } catch (error) {
      console.error('Failed to create draft project:', error);
      isCreatingDraft.current = false;
    }
  };

  // Handle form submission with patch operations for edit mode
  const handleFormSubmit = async (values: ProjectCreateRequest, isDraft: boolean = false) => {
    // Validate based on draft mode
    const errors = isDraft ? validateDraft(values) : validateSubmission(values);

    if (Object.keys(errors).length > 0) {
      formik.setErrors(errors);
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      scrollToFirstError(errors);
      return;
    }

    const cleanData = cleanFormData(values);

    if (isEdit && initialData) {
      // Edit mode: delegate to parent (ProjectEdit.tsx handles patch)
      await onSubmit(cleanData, isDraft);
    } else if (draftProjectId) {
      // Draft was auto-created: PATCH the draft instead of creating a new project
      const initialCleanData = cleanFormData(getInitialValues());
      const patchOperations = createProjectPatchOperations(
        initialCleanData as unknown as Project,
        cleanData
      );

      // Apply patch if there are changes
      if (patchOperations.length > 0) {
        await patchProjectMutation.mutateAsync({
          id: draftProjectId,
          operations: patchOperations,
        });
      }

      // Update status to Submitted if not saving as draft
      if (!isDraft) {
        await updateStatusMutation.mutateAsync({
          id: draftProjectId,
          statusUpdate: { status: ProjectStatus.Submitted },
        });
      }

      // Call onSubmit to handle navigation/alerts (pass the draftProjectId)
      await onSubmit({ ...cleanData, id: draftProjectId } as ProjectCreateRequest, isDraft);
    } else {
      // For create mode (no draft created yet), submit the full data
      await onSubmit(cleanData, isDraft);
    }
  };

  // Handle draft submission
  const handleDraftSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Force validation to show errors
    const errors = validateDraft(formik.values);
    if (Object.keys(errors).length > 0) {
      formik.setErrors(errors);
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      scrollToFirstError(errors);
      return;
    }
    await handleFormSubmit(formik.values, true);
  };

  // Handle main submit
  const handleMainSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Force validation to show errors
    const errors = validateSubmission(formik.values);
    if (Object.keys(errors).length > 0) {
      formik.setErrors(errors);
      formik.setTouched(
        Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      );
      scrollToFirstError(errors);
      return;
    }
    formik.handleSubmit(e as any);
  };

  // Company dialog handlers
  const handleCompanyDialogClose = () => {
    setCompanyDialogOpen(false);
  };

  const handleCreateCompany = async (
    companyData: CompanyCreateDTO
  ) => {
    try {
      // Create company
      const createdCompany = await createCompanyMutation.mutateAsync(companyData);
      createdCompanyIdRef.current = createdCompany.id;

      // Note: Address creation is handled by CompanyForm internally if needed

      // Close the dialog
      setCompanyDialogOpen(false);
    } catch (error) {
      console.error('Error creating company:', error);
    }
  };


  return (
    <Box>
      {/* Welcome Banner for Clients */}
      {isClient && !isEdit && (
        <Alert
          severity="info"
          sx={{
            borderRadius: '12px',
            mb: 3,
            px: 3,
            py: 2.5,
            fontSize: '16px',
            '& .MuiAlert-icon': {
              fontSize: 24,
            },
          }}
        >
          {t('project.welcomeMessage')}
        </Alert>
      )}
      
      <Paper 
        elevation={0} 
        sx={{ 
          p: isMobile ? 2 : 5, 
          backgroundColor: zoullooColors.background.default,
          border: `1px solid ${zoullooColors.grey[250]}`,
          borderRadius: 2,
          mb: 3,
        }}
      >
        {/* Header */}
        <Box mb={isMobile ? 3 : 4}>
          <Typography variant={isMobile ? "h5" : "h4"} fontWeight={700} gutterBottom sx={{ mb: isMobile ? 2 : 3 }}>
            {isEdit ? t('project.editProject') : t('project.createProjectTitle', { company: selectedCompany?.legalName || '{'+ t('project.company')+'}' })}
          </Typography>
          
          {!isEdit && (
            <>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
                {t('project.projectBriefIntro')}
              </Typography>
              
              <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                {t('project.projectContactInfo')}
              </Typography>
            </>
          )}
        </Box>

        <form onSubmit={formik.handleSubmit} noValidate>
          {/* Project Presentation Section */}
          <Box sx={{ mb: isMobile ? 4 : 5 }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600} gutterBottom sx={{ mb: isMobile ? 2 : 3 }}>
              {t('project.presentationTitle')}
            </Typography>

            <Grid container spacing={isMobile ? 2 : 3}>
              {/* Project Title */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                  {t('project.projectTitle')} *
                </Typography>
                <TextFieldWithCounter
                  fullWidth
                  name="name"
                  maxLength={200}
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                  variant="outlined"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: isMobile ? '40px' : '48px',
                    }
                  }}
                />
              </Grid>

              {/* Company */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                  {t('project.company')} *
                </Typography>
                <CompanyAutocomplete
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label=""
                  placeholder={isClient ? selectedCompany?.legalName : t('company.select')}
                  error={formik.touched.companyId && formik.errors.companyId ? String(formik.errors.companyId) : undefined}
                  helperText={isClient && !isEdit ? t('project.companyPreselected') : undefined}
                  disabled={isClient}
                  required
                  showCreateButton={isInternalUser}
                  onCreateNew={() => setCompanyDialogOpen(true)}
                />
              </Grid>

              {/* Admin/CSM Only Fields - Two rows */}
              {isInternalUser && (
                <>
                  {/* Domain Selection */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.domain')} *
                    </Typography>
                    <FormControl fullWidth required error={formik.touched.domain && Boolean(formik.errors.domain)} size={isMobile ? 'small' : 'medium'}>
                      <Select
                        name="domain"
                        value={formik.values.domain || ''}
                        onChange={(e) => handleChange('domain', e.target.value)}
                        onBlur={formik.handleBlur}
                        displayEmpty
                      >
                        {Object.values(ProjectDomain).map((domain) => (
                          <MenuItem key={domain} value={domain}>
                            {translateProjectDomain(domain)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Mission Type */}
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.missionType')}
                    </Typography>
                    <FormControl
                      fullWidth
                      error={Boolean(formik.errors.missionType && formik.touched.missionType)}
                    >
                      <Select
                        name="missionType"
                        value={formik.values.missionType || ''}
                        onChange={(e) => handleChange('missionType', e.target.value)}
                        onBlur={formik.handleBlur}
                        displayEmpty
                      >
                        <MenuItem value="">{t('common.select')}</MenuItem>
                        {Object.values(MissionType).map((missionType) => (
                          <MenuItem key={missionType} value={missionType}>
                            {translateMissionType(missionType)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Fee Percentage - Honoraires */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      Honoraires *
                    </Typography>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      name="feePercentage"
                      value={formik.values.feePercentage || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseFloat(e.target.value);
                        if ((value >= 0 && value <= 100) || e.target.value === '') {
                          handleChange('feePercentage', value || undefined);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      error={formik.touched.feePercentage && Boolean(formik.errors.feePercentage)}
                      helperText={formik.touched.feePercentage && formik.errors.feePercentage}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: isMobile ? '40px' : '48px',
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                    />
                  </Grid>

                  {/* External Costs Fee Percentage */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.externalCostsFeePercentage')}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      name="externalCostsFeePercentage"
                      value={formik.values.externalCostsFeePercentage || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseFloat(e.target.value);
                        if ((value >= 0 && value <= 100) || e.target.value === '') {
                          handleChange('externalCostsFeePercentage', value || undefined);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: isMobile ? '40px' : '48px',
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                    />
                  </Grid>

                  {/* Expenses Fee Percentage */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.expensesFeePercentage')}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      name="expensesFeePercentage"
                      value={formik.values.expensesFeePercentage || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseFloat(e.target.value);
                        if ((value >= 0 && value <= 100) || e.target.value === '') {
                          handleChange('expensesFeePercentage', value || undefined);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: isMobile ? '40px' : '48px',
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                    />
                  </Grid>

                  {/* Processing Fees */}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.processingFees')}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      name="processingFees"
                      value={formik.values.processingFees ?? ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const raw = e.target.value;
                        // Empty input clears the fee (sends null so the patch emits an explicit replace),
                        // and a typed 0 is preserved instead of being coerced to undefined.
                        if (raw === '') {
                          handleChange('processingFees', null);
                          return;
                        }
                        const value = parseFloat(raw);
                        if (!isNaN(value) && value >= 0) {
                          handleChange('processingFees', value);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      size={isMobile ? 'small' : 'medium'}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: isMobile ? '40px' : '48px',
                        }
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        inputProps: { min: 0 },
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Objectives */}
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2} mt={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('project.objectivesQuestion')} *
                  </Typography>
                  <Tooltip title={t('project.objectivesPlaceholder')}>
                    <HelpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Box data-field="objectives">
                  <HtmlEditor
                    content={formik.values.objectives}
                    onUpdate={({ editor }) => {
                      const newValue = editor.getHTML();
                      formik.setFieldValue('objectives', newValue);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched('objectives', true);
                    }}
                    placeholder={t('project.objectivesPlaceholder')}
                    maxLength={4000}
                    error={formik.touched.objectives && Boolean(formik.errors.objectives)}
                    sx={{
                      '& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror': {
                        height: isMobile ? '120px !important' : '150px !important'
                      }
                    }}
                  />
                </Box>
                {formik.touched.objectives && formik.errors.objectives && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formik.errors.objectives}
                  </Typography>
                )}
              </Grid>

              {/* Presentation */}
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2} mt={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('project.presentationQuestion')} *
                  </Typography>
                  <Tooltip title={t('project.presentationAndNeedsPlaceholder')}>
                    <HelpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Box data-field="presentationAndNeeds">
                  <HtmlEditor
                    content={formik.values.presentationAndNeeds}
                    onUpdate={({ editor }) => {
                      const newValue = editor.getHTML();
                      formik.setFieldValue('presentationAndNeeds', newValue);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched('presentationAndNeeds', true);
                    }}
                    placeholder={t('project.presentationAndNeedsPlaceholder')}
                    maxLength={6500}
                    error={formik.touched.presentationAndNeeds && Boolean(formik.errors.presentationAndNeeds)}
                    sx={{
                      '& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror': {
                        height: isMobile ? '120px !important' : '150px !important'
                      }
                    }}
                  />
                </Box>
                {formik.touched.presentationAndNeeds && formik.errors.presentationAndNeeds && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formik.errors.presentationAndNeeds}
                  </Typography>
                )}
              </Grid>

              {/* Hard Skills Required */}
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2} mt={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('project.hardSkillsQuestion')} *
                  </Typography>
                  <Tooltip title={t('project.hardSkillsPlaceholder')}>
                    <HelpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Box data-field="talentHardSkills">
                  <HtmlEditor
                    content={formik.values.talentHardSkills}
                    onUpdate={({ editor }) => {
                      const newValue = editor.getHTML();
                      formik.setFieldValue('talentHardSkills', newValue);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched('talentHardSkills', true);
                    }}
                    placeholder={t('project.hardSkillsPlaceholder')}
                    maxLength={2000}
                    error={formik.touched.talentHardSkills && Boolean(formik.errors.talentHardSkills)}
                    sx={{
                      '& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror': {
                        height: isMobile ? '120px !important' : '150px !important'
                      }
                    }}
                  />
                </Box>
                {formik.touched.talentHardSkills && formik.errors.talentHardSkills && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formik.errors.talentHardSkills}
                  </Typography>
                )}
              </Grid>

              {/* Soft Skills Required */}
              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={1} mb={2} mt={3}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('project.softSkillsQuestion')} *
                  </Typography>
                  <Tooltip title={t('project.softSkillsPlaceholder')}>
                    <HelpIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </Tooltip>
                </Box>
                <Box data-field="talentSoftSkills">
                  <HtmlEditor
                    content={formik.values.talentSoftSkills}
                    onUpdate={({ editor }) => {
                      const newValue = editor.getHTML();
                      formik.setFieldValue('talentSoftSkills', newValue);
                    }}
                    onBlur={() => {
                      formik.setFieldTouched('talentSoftSkills', true);
                    }}
                    placeholder={t('project.softSkillsPlaceholder')}
                    maxLength={2000}
                    error={formik.touched.talentSoftSkills && Boolean(formik.errors.talentSoftSkills)}
                    sx={{
                      '& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror': {
                        height: isMobile ? '120px !important' : '150px !important'
                      }
                    }}
                  />
                </Box>
                {formik.touched.talentSoftSkills && formik.errors.talentSoftSkills && (
                  <Typography color="error" variant="body2" sx={{ mt: 1 }}>
                    {formik.errors.talentSoftSkills}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Box>

          {/* Mission Modalities Section */}
          <Box sx={{ mt: isMobile ? 4 : 6, mb: isMobile ? 4 : 5 }}>
            <Typography variant={isMobile ? "h6" : "h5"} fontWeight={600} gutterBottom sx={{ mb: isMobile ? 2 : 3 }}>
              {t('project.missionModalities')}
            </Typography>

            <Grid container spacing={isMobile ? 2 : 3}>
              <Grid size={{ xs: 12, md: 3.5 }}>
                <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500, whiteSpace: 'nowrap' }}>
                  {t('project.startDateQuestion')} *
                </Typography>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale={i18n.language === "fr" ? "fr" : "en"}
                >
                  <DatePicker
                    value={formik.values.desiredStartDate ? dayjs(formik.values.desiredStartDate) : null}
                    onChange={(newValue) => {
                      if (newValue === null || newValue?.isValid()) {
                        formik.setFieldValue('desiredStartDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                      }
                    }}
                    {...(!isEdit && { minDate: dayjs(today) })}
                    maxDate={dayjs(maxDateString)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        name: 'desiredStartDate',
                        error: formik.touched.desiredStartDate && Boolean(formik.errors.desiredStartDate),
                        helperText: formik.touched.desiredStartDate && formik.errors.desiredStartDate,
                        onBlur: formik.handleBlur
                      }
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* End Date - CSM/Admin Only */}
              {isInternalUser && (
                <Grid size={{ xs: 12, md: 3.5 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500, whiteSpace: 'nowrap' }}>
                    {t('project.endDateQuestion')}
                  </Typography>
                  <LocalizationProvider
                    dateAdapter={AdapterDayjs}
                    adapterLocale={i18n.language === "fr" ? "fr" : "en"}
                  >
                    <DatePicker
                      value={formik.values.endDate ? dayjs(formik.values.endDate) : null}
                      onChange={(newValue) => {
                        if (newValue === null || newValue?.isValid()) {
                          formik.setFieldValue('endDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                        }
                      }}
                      minDate={dayjs(formik.values.desiredStartDate || today)}
                      maxDate={dayjs(maxDateString)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          name: 'endDate',
                          error: formik.touched.endDate && Boolean(formik.errors.endDate),
                          helperText: formik.touched.endDate && formik.errors.endDate,
                          onBlur: formik.handleBlur
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              )}

              <Grid size={12}>
                <Box display="flex" alignItems="center" gap={2} sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {t('project.teamIntegrationQuestion')}
                  </Typography>
                  <FormGroup>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" color="text.secondary">
                        {t('common.no')}
                      </Typography>
                      <Switch
                        checked={formik.values.teamIntegration}
                        onChange={(e) => handleChange('teamIntegration', e.target.checked)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: zoullooColors.grey[550],
                          },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                            backgroundColor: zoullooColors.grey[550],
                          },
                        }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t('common.yes')}
                      </Typography>
                    </Box>
                  </FormGroup>
                </Box>
              </Grid>

              <Grid size={12}>
                <Typography variant="body2" gutterBottom sx={{ mt: 3, mb: 2, fontWeight: 500 }}>
                  {t('project.locationPreference')} *
                </Typography>
                <Box 
                  display="flex" 
                  gap={2} 
                  flexWrap="wrap"
                  data-field="location"
                  sx={{
                    padding: 1,
                    borderRadius: 1,
                    border: formik.touched.location && formik.errors.location 
                      ? `2px solid ${theme.palette.error.main}` 
                      : 'none',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.location === ProjectLocation.OnSite}
                        onChange={() => handleChange('location', formik.values.location === ProjectLocation.OnSite ? undefined : ProjectLocation.OnSite)}
                        sx={{ 
                          '&.Mui-checked': { 
                            color: zoullooColors.success.selected 
                          } 
                        }}
                      />
                    }
                    label={t('project.onSite')}
                    sx={{ mr: 3 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.location === ProjectLocation.Remote}
                        onChange={() => handleChange('location', formik.values.location === ProjectLocation.Remote ? undefined : ProjectLocation.Remote)}
                        sx={{ 
                          '&.Mui-checked': { 
                            color: zoullooColors.success.selected 
                          } 
                        }}
                      />
                    }
                    label={t('project.remote')}
                    sx={{ mr: 3 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.location === ProjectLocation.Hybrid}
                        onChange={() => handleChange('location', formik.values.location === ProjectLocation.Hybrid ? undefined : ProjectLocation.Hybrid)}
                        sx={{ 
                          '&.Mui-checked': { 
                            color: zoullooColors.success.selected 
                          } 
                        }}
                      />
                    }
                    label={t('project.hybrid')}
                    sx={{ mr: 3 }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.location === ProjectLocation.NoPreference}
                        onChange={() => handleChange('location', formik.values.location === ProjectLocation.NoPreference ? undefined : ProjectLocation.NoPreference)}
                        sx={{ 
                          '&.Mui-checked': { 
                            color: zoullooColors.success.selected 
                          } 
                        }}
                      />
                    }
                    label={t('project.noPreference')}
                  />
                </Box>
                {formik.touched.location && formik.errors.location && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                    {formik.errors.location}
                  </Typography>
                )}
              </Grid>

              {formik.values.location === ProjectLocation.Hybrid && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                    {t('project.hybridDetailsHelp')} *
                  </Typography>
                  <HtmlEditor
                    content={formik.values.hybridDetails || ''}
                    onUpdate={({ editor }) => {
                      const content = editor.getHTML();
                      handleChange('hybridDetails', content === '<p></p>' ? '' : content);
                    }}
                    onBlur={() => formik.setFieldTouched('hybridDetails', true)}
                    placeholder={t('project.hybridDetailsPlaceholder')}
                    maxLength={500}
                    error={formik.touched.hybridDetails && Boolean(formik.errors.hybridDetails)}
                    sx={{
                      '& .MuiTiptap-FieldContainer-root': {
                        backgroundColor: zoullooColors.grey[25],
                        '&:hover': {
                          backgroundColor: zoullooColors.grey[75],
                        },
                        '&.MuiTiptap-FieldContainer-focused': {
                          backgroundColor: zoullooColors.background.default,
                        }
                      },
                      '& .MuiTiptap-FieldContainer-root .MuiTiptap-RichTextContent-root .ProseMirror': {
                        height: '75px !important'
                      }
                    }}
                  />
                  {formik.touched.hybridDetails && formik.errors.hybridDetails && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {formik.errors.hybridDetails}
                    </Typography>
                  )}
                </Grid>
              )}

              {formik.values.location && (
                <Grid size={12}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 2, fontWeight: 500, mt: 3 }}>
                    {t('project.prestationTypeQuestion')} *
                  </Typography>
                  <Box
                    data-field="type"
                    sx={{
                      display: 'inline-block',
                      padding: formik.touched.type && formik.errors.type ? 1 : 0,
                      borderRadius: 1,
                      border: formik.touched.type && formik.errors.type 
                        ? `2px solid ${theme.palette.error.main}` 
                        : 'none',
                    }}
                  >
                  <ToggleButtonGroup
                    value={formik.values.type}
                    exclusive
                    onChange={(_, newValue) => newValue && handleChange('type', newValue)}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      width: { xs: '100%', sm: 'auto' },
                      '& .MuiToggleButtonGroup-grouped': {
                        margin: 0,
                        width: { xs: '100%', sm: '280px' },
                        minWidth: { xs: '100%', sm: '280px' },
                        '&:not(:first-of-type)': {
                          borderLeft: `1px solid ${zoullooColors.grey[250]}`,
                          marginLeft: 0,
                        },
                        '&:not(:last-of-type)': {
                          borderRight: `1px solid ${zoullooColors.grey[250]}`,
                          marginRight: 0,
                        },
                        '&.Mui-selected': {
                          zIndex: 1,
                          position: 'relative',
                          border: `2px solid ${zoullooColors.success.selected} !important`,
                        },
                        '&.Mui-selected:not(:last-of-type)': {
                          borderRight: `2px solid ${zoullooColors.success.selected} !important`,
                        }
                      }
                    }}
                  >
                    <ToggleButton value={ProjectType.FixedPrice} sx={{ height: '48px', fontSize: '15px'}} >
                      {t('project.fixedPrice')}
                    </ToggleButton>
                    <ToggleButton value={ProjectType.TimeAndMaterial} sx={{height: '48px', fontSize: '15px'}}>
                      {t('project.timeAndMaterial')}
                    </ToggleButton>
                  </ToggleButtonGroup>
                  </Box>
                  {formik.touched.type && formik.errors.type && (
                    <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                      {formik.errors.type}
                    </Typography>
                  )}
                </Grid>
              )}

              {/* Budget for Fixed Price */}
              {formik.values.type === ProjectType.FixedPrice && (
                <Grid size={{ xs: 12, md: 3 }}>
                  <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                    {t('project.budgetQuestion')}
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    name="budget"
                    value={formik.values.budget || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const value = parseFloat(e.target.value);
                      if (value >= 0 || e.target.value === '') {
                        handleChange('budget', value || undefined);
                      }
                    }}
                    onBlur={formik.handleBlur}
                    error={formik.touched.budget && Boolean(formik.errors.budget)}
                    helperText={(formik.touched.budget && formik.errors.budget) || t('project.budgetHelp')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        height: '48px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EuroIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: <InputAdornment position="end">HT</InputAdornment>,
                      inputProps: { min: 0 },
                    }}
                  />
                </Grid>
              )}

              {/* Time & Material Fields - Progressive display */}
              {formik.values.type === ProjectType.TimeAndMaterial && (
                <>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Typography variant="body2" gutterBottom sx={{ mb: 1, fontWeight: 500 }}>
                      {t('project.desiredTJM')}
                    </Typography>
                    <TextField
                      fullWidth
                      type="number"
                      value={formik.values.dailyRate || ''}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = parseFloat(e.target.value);
                        if (value >= 0 || e.target.value === '') {
                          handleChange('dailyRate', value || undefined);
                        }
                      }}
                      error={formik.touched.dailyRate && Boolean(formik.errors.dailyRate)}
                      helperText={formik.touched.dailyRate && formik.errors.dailyRate}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: '48px',
                        }
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EuroIcon color="action" />
                          </InputAdornment>
                        ),
                        endAdornment: <InputAdornment position="end">/jour</InputAdornment>,
                        inputProps: { min: 0 },
                      }}
                    />
                  </Grid>

                  <Grid size={12}>
                    <Box 
                      display="flex" 
                      gap={1.5} 
                      alignItems="center"
                      flexWrap="wrap"
                      sx={{ mt: 2 }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                         {t('project.indicateDuration')} *
                      </Typography>
                      <TextField
                        type="number"
                        value={formik.values.duration || ''}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          const value = parseFloat(e.target.value);
                          if (value >= 0 || e.target.value === '') {
                            handleChange('duration', value || undefined);
                          }
                        }}
                        error={formik.touched.duration && Boolean(formik.errors.duration)}
                        helperText={formik.touched.duration && formik.errors.duration}
                        size="small"
                        sx={{
                          width: '80px',
                          '& .MuiOutlinedInput-root': {
                            height: '36px',
                          }
                        }}
                        InputProps={{
                          inputProps: { min: 0.1, step: 0.1 },
                        }}
                      />
                      <Box
                        data-field="durationType"
                        sx={{
                          display: 'inline-block',
                          padding: formik.touched.durationType && formik.errors.durationType ? 0.5 : 0,
                          borderRadius: 1,
                          border: formik.touched.durationType && formik.errors.durationType 
                            ? `2px solid ${theme.palette.error.main}` 
                            : 'none',
                        }}
                      >
                      <ToggleButtonGroup
                        value={formik.values.durationType}
                        exclusive
                        onChange={(_, newValue) => {
                          if (newValue) {
                            handleChange('durationType', newValue);
                            // Reset fullTime when changing duration type
                            formik.setFieldValue('fullTime', undefined);
                            formik.setFieldValue('daysPerPeriod', undefined);
                          }
                        }}
                        sx={{
                          display: 'flex',
                          gap: 1,
                          '& .MuiToggleButtonGroup-grouped': {
                            margin: 0,
                            minWidth: '140px',
                            fontSize: '14px',
                            '&:not(:first-of-type)': {
                              borderLeft: `1px solid ${zoullooColors.grey[250]}`,
                              marginLeft: 0,
                            },
                            '&:not(:last-of-type)': {
                              borderRight: `1px solid ${zoullooColors.grey[250]}`,
                              marginRight: 0,
                            },
                            '&.Mui-selected': {
                              zIndex: 1,
                              position: 'relative',
                              border: `2px solid ${zoullooColors.success.selected} !important`,
                            },
                            '&.Mui-selected:not(:last-of-type)': {
                              borderRight: `2px solid ${zoullooColors.success.selected} !important`,
                            }
                          }
                        }}
                      >
                        <ToggleButton value={DurationType.Weeks} sx={{ height: '36px' }}>
                          {t('project.weeks')}
                        </ToggleButton>
                        <ToggleButton value={DurationType.Months} sx={{ height: '36px' }}>
                          {t('project.months')}
                        </ToggleButton>
                      </ToggleButtonGroup>
                      </Box>
                    </Box>
                    {formik.touched.durationType && formik.errors.durationType && (
                      <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                        {formik.errors.durationType}
                      </Typography>
                    )}
                  </Grid>

                  {formik.values.durationType && (
                    <Grid size={12}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Box sx={{ width: '135px' }} /> 
                        <Box
                          data-field="fullTime"
                          sx={{
                            display: 'inline-block',
                            padding: formik.touched.fullTime && formik.errors.fullTime ? 0.5 : 0,
                            borderRadius: 1,
                            border: formik.touched.fullTime && formik.errors.fullTime 
                              ? `2px solid ${theme.palette.error.main}` 
                              : 'none',
                          }}
                        >
                        <ToggleButtonGroup
                          value={formik.values.fullTime === true ? 'fullTime' : formik.values.fullTime === false ? 'partTime' : undefined}
                          exclusive
                          onChange={(_, newValue) => {
                            if (newValue === 'fullTime') {
                              handleChange('fullTime', true);
                            } else if (newValue === 'partTime') {
                              handleChange('fullTime', false);
                            }
                          }}
                          sx={{
                            display: 'flex',
                            gap: 1,
                            '& .MuiToggleButtonGroup-grouped': {
                              margin: 0,
                              minWidth: '140px',
                              fontSize: '14px',
                              '&:not(:first-of-type)': {
                                borderLeft: `1px solid ${zoullooColors.grey[250]}`,
                                marginLeft: 0,
                              },
                              '&:not(:last-of-type)': {
                                borderRight: `1px solid ${zoullooColors.grey[250]}`,
                                marginRight: 0,
                              },
                              '&.Mui-selected': {
                                zIndex: 1,
                                position: 'relative',
                                border: `2px solid ${zoullooColors.success.selected} !important`,
                              },
                              '&.Mui-selected:not(:last-of-type)': {
                                borderRight: `2px solid ${zoullooColors.success.selected} !important`,
                              }
                            }
                          }}
                        >
                          <ToggleButton value="fullTime" sx={{height: '36px'}}>
                            {t('project.fullTime')}
                          </ToggleButton>
                          <ToggleButton value="partTime" sx={{height: '36px'}}>
                            {t('project.partTime')}
                          </ToggleButton>
                        </ToggleButtonGroup>
                        </Box>
                      </Box>
                      {formik.touched.fullTime && formik.errors.fullTime && (
                        <Box display="flex" justifyContent="flex-start" mt={1}>
                          <Box sx={{ width: '135px' }} />
                          <Typography color="error" variant="caption">
                            {formik.errors.fullTime}
                          </Typography>
                        </Box>
                      )}

                      {formik.values.fullTime === true && formik.values.duration && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {formik.values.durationType === DurationType.Weeks
                            ? `Un projet à temps complet sur ${formik.values.duration} semaine(s) équivaut à environ ${formik.values.duration * 5} jours de mission.`
                            : `Un projet à temps complet sur ${formik.values.duration} mois équivaut à environ ${formik.values.duration * 20} jours de mission.`}
                        </Typography>
                      )}
                    </Grid>
                  )}

                  {formik.values.fullTime === false && (
                    <Grid size={12}>
                      <Box display="flex" gap={2} alignItems="center" sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                         {formik.values.durationType === DurationType.Weeks
                          ? t('project.daysPerWeek')
                          : t('project.daysPerMonth')} *
                        </Typography>
                        <TextField
                          type="number"
                          value={formik.values.daysPerPeriod || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const value = parseFloat(e.target.value) || 0;
                            const maxDays = formik.values.durationType === DurationType.Weeks ? 5 : 20;
                            if (value >= 0 && value <= maxDays) {
                              handleChange('daysPerPeriod', value || undefined);
                            }
                          }}
                          error={formik.touched.daysPerPeriod && Boolean(formik.errors.daysPerPeriod)}
                          helperText={formik.touched.daysPerPeriod && formik.errors.daysPerPeriod}
                          size="small"
                          sx={{
                            width: '80px',
                            '& .MuiOutlinedInput-root': {
                              height: '36px',
                            }
                          }}
                          InputProps={{
                            inputProps: {
                              min: 0.1,
                              step: 0.1,
                              max: formik.values.durationType === DurationType.Weeks ? 5 : 20,
                            },
                          }}
                        />
                      </Box>
                      
                      {formik.values.duration && formik.values.daysPerPeriod && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                          {formik.values.durationType === DurationType.Weeks
                            ? `Un projet à temps partiel sur ${formik.values.duration} semaine(s) équivaut à environ ${formik.values.duration * formik.values.daysPerPeriod} jours de mission.`
                            : `Un projet à temps partiel sur ${formik.values.duration} mois équivaut à environ ${formik.values.duration * formik.values.daysPerPeriod} jours de mission.`}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </>
              )}

              {/* Fee Percentage moved to top row for admin users */}


            </Grid>
          </Box>

          {/* Documents Section - Create mode */}
          {!isEdit && (
            <Box sx={{ mb: isMobile ? 4 : 5 }}>
              {draftProjectId ? (
                <ProjectDocuments
                  projectId={draftProjectId}
                  projectStatus={ProjectStatus.Draft}
                  createdById={user.id}
                  autoOpenAdd={true}
                />
              ) : (
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    mb: 3,
                    opacity: (!formik.values.name?.trim() || !formik.values.companyId) ? 0.5 : 1,
                    pointerEvents: (!formik.values.name?.trim() || !formik.values.companyId) ? 'none' : 'auto'
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h4" color={zoullooColors.secondary.dark}>
                      {t('project.documents.title')}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={createProjectMutation.isPending ? <CircularProgress size={16} /> : <AddIcon />}
                      onClick={handleCreateDraftForDocument}
                      disabled={createProjectMutation.isPending || !formik.values.name?.trim() || !formik.values.companyId}
                      sx={{
                        borderColor: zoullooColors.secondary.main,
                        color: zoullooColors.secondary.main,
                        backgroundColor: 'white',
                        '&:hover': {
                          borderColor: zoullooColors.secondary.main,
                          backgroundColor: zoullooColors.secondary.alpha,
                        },
                      }}
                    >
                      {t('project.documents.addDocument')}
                    </Button>
                  </Box>
                  <Divider sx={{ mb: 3 }} />
                  <Alert severity="info">
                    {t('project.documents.noDocuments')}
                  </Alert>
                </Paper>
              )}
            </Box>
          )}

          {/* Documents Section - Edit mode */}
          {isEdit && initialData?.id && (
            <Box sx={{ mb: isMobile ? 4 : 5 }}>
              <ProjectDocuments
                projectId={initialData.id}
                projectStatus={initialData.status || ProjectStatus.Draft}
                createdById={initialData.createdById}
              />
            </Box>
          )}

          {/* Submit Buttons */}
          <Box 
            display="flex" 
            gap={isMobile ? 1.5 : 2} 
            mt={isMobile ? 3 : 5}
            flexDirection={isMobile ? 'column-reverse' : 'row'}
            sx={{
              '& > button': {
                width: isMobile ? '100%' : 'auto'
              }
            }}
          >
            {/* Main action button */}
            {isClient && isEdit && initialData?.status === ProjectStatus.Draft ? (
              // Client editing draft: Show "Submit project" button
              <Button
                variant="contained"
                onClick={async (e) => {
                  e.preventDefault();
                  const errors = validateSubmission(formik.values);
                  if (Object.keys(errors).length > 0) {
                    formik.setErrors(errors);
                    formik.setTouched(
                      Object.keys(errors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
                    );
                    scrollToFirstError(errors);
                    return;
                  }
                  await handleFormSubmit(formik.values, false);
                  if (onSubmitProject) {
                    const cleanData = cleanFormData(formik.values);
                    onSubmitProject(cleanData);
                  }
                }}
                disabled={formik.isSubmitting}
                sx={{
                  backgroundColor: zoullooColors.secondary.main,
                  '&:hover': {
                    backgroundColor: zoullooColors.secondary.dark,
                  },
                  '&:disabled': {
                    backgroundColor: zoullooColors.grey[150],
                    color: zoullooColors.grey[450],
                  },
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 1.25 : 1.5,
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 500,
                  minWidth: isMobile ? 'auto' : '180px',
                }}
              >
                {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : t('project.submitProject')}
              </Button>
            ) : (
              // Default button for create/update
              <Button
                variant="contained"
                onClick={handleMainSubmit}
                disabled={formik.isSubmitting}
                sx={{
                  backgroundColor: zoullooColors.secondary.main,
                  '&:hover': {
                    backgroundColor: zoullooColors.secondary.dark,
                  },
                  '&:disabled': {
                    backgroundColor: zoullooColors.grey[150],
                    color: zoullooColors.grey[450],
                  },
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 1.25 : 1.5,
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 500,
                  minWidth: isMobile ? 'auto' : '180px',
                }}
              >
                {formik.isSubmitting ? <CircularProgress size={20} color="inherit" /> : (isEdit ? t('project.updateProject') : t('project.createProjectButton'))}
              </Button>
            )}
            
            {/* Draft button: Show for new projects OR when editing draft */}
            {(!isEdit || (initialData && initialData.status === ProjectStatus.Draft)) && (
              <Button
                variant="outlined"
                onClick={handleDraftSubmit}
                disabled={formik.isSubmitting}
                sx={{
                  borderColor: zoullooColors.secondary.main,
                  borderWidth: '1px',
                  color: zoullooColors.secondary.main,
                  '&:hover': {
                    borderColor: zoullooColors.secondary.dark,
                    borderWidth: '1px',
                    backgroundColor: zoullooColors.secondary.alpha,
                  },
                  '&:disabled': {
                    borderColor: zoullooColors.grey[150],
                    borderWidth: '1px',
                    color: zoullooColors.grey[450],
                  },
                  textTransform: 'none',
                  borderRadius: '8px',
                  px: isMobile ? 3 : 4,
                  py: isMobile ? 1.25 : 1.5,
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 500,
                  minWidth: isMobile ? 'auto' : '200px',
                }}
              >
                {isClient && isEdit ? t('project.updateDraft') : t('project.saveDraft')}
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={() => window.history.back()}
              sx={{
                borderColor: zoullooColors.grey[250],
                borderWidth: '1px',
                color: zoullooColors.grey[550],
                backgroundColor: 'transparent',
                '&:hover': {
                  borderColor: zoullooColors.grey[400],
                  borderWidth: '1px',
                  backgroundColor: zoullooColors.grey[50],
                },
                textTransform: 'none',
                borderRadius: '8px',
                px: isMobile ? 3 : 4,
                py: isMobile ? 1.25 : 1.5,
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: 500,
                minWidth: isMobile ? 'auto' : '120px',
              }}
            >
              {t('project.cancel')}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Final Info Banner for Clients */}
      {isClient && (
        <Box mt={3}>
          <Alert
            severity="info"
            sx={{
              borderRadius: '12px',
              px: 3,
              py: 2.5,
              fontSize: '16px',
              '& .MuiAlert-icon': {
                fontSize: 24,
              },
            }}
          >
            {t('project.verifyInfoMessage')}
          </Alert>
        </Box>
      )}

      {/* Company Creation Dialog */}
      {isInternalUser && (
        <Dialog
          open={companyDialogOpen}
          onClose={handleCompanyDialogClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle sx={{ color: zoullooColors.secondary.main }}>
            {t('company.createNewCompany')}
          </DialogTitle>
          <DialogContent dividers>
            <CompanyForm
              initialData={{}}
              onSubmit={handleCreateCompany}
              isSubmitting={createCompanyMutation.isPending}
              submitButtonText={t('company.create')}
              isClientOnboarding={false}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCompanyDialogClose}>
              {t('common.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default ProjectForm;